import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Header from "../components/Header";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import * as XLSX from 'xlsx';
import CustomButton from "../pages/CustomButton"

const SOBTargetReport = () => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedQuarter, setSelectedQuarter] = useState("Q1-2024");
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const printRef = useRef();

  // Function to get quarter date range
  const getQuarterDateRange = (quarterWithYear) => {
    const [quarter, year] = quarterWithYear.split('-');
    let startDate, endDate;
    
    switch(quarter) {
      case 'Q1':
        startDate = `01/Apr/${year}`;
        endDate = `30/Jun/${year}`;
        break;
      case 'Q2':
        startDate = `01/Jul/${year}`;
        endDate = `30/Sep/${year}`;
        break;
      case 'Q3':
        startDate = `01/Oct/${year}`;
        endDate = `31/Dec/${year}`;
        break;
      case 'Q4':
        startDate = `01/Jan/${year}`;
        endDate = `31/Mar/${parseInt(year) + 1}`;
        break;
      default:
        startDate = `01/Apr/${year}`;
        endDate = `30/Jun/${year}`;
    }
    
    return `${startDate}-${endDate}`;
  };

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders()
        });
        setStates(response.data);
      } catch (error) {
        console.error("Error fetching states:", error);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    const fetchDivisions = async () => {
      if (selectedState) {
        try {
          const response = await axios.get(
            `${BASE_URL}/api/divisions?state_id=${selectedState}`,
            { headers: getAuthHeaders() }
          );
          setDivisions(response.data);
          setError(null);
        } catch (error) {
          console.error("Error fetching divisions:", error);
          setError("Failed to load divisions");
        }
      } else {
        setDivisions([]);
        setSelectedDivision("");
      }
    };
    fetchDivisions();
  }, [selectedState]);

  const generateReport = async () => {
    if (!selectedState || !selectedDivision) {
      alert("Please select both state and division");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const division = divisions.find(d => d.id == selectedDivision);
      const divisionName = division?.name;
      const quarterDateRange = getQuarterDateRange(selectedQuarter);

      const response = await axios.get(
        `${BASE_URL}/api/sob-targets/employee_target_report`,
        {
          params: {
            state_id: selectedState,
            division: divisionName,
            quarter: quarterDateRange,
          },
          headers: getAuthHeaders()
        }
      );
      setReportData(response.data);
    } catch (error) {
      console.error("Error fetching report:", error);
      setError("Failed to generate report");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    return value ? parseFloat(value).toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }) : '0.00';
  };

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const handleExportExcel = () => {
    if (!reportData.length) return;

    const tableData = reportData.map((item, index) => ({
      "S.No": index + 1,
      "Empid": item.employee_id,
      "Employee": item.employee_name,
      "Division": item.division,
      "State": item.state,
      "HQ": item.hq,
      "Designation": item.designation,
      "DOJ": formatDate(item.final_joining_date),
      "DOR": formatDate(item.resign_date),
      "Phone": item.phone_no || '-',
      "Target": item.target,
      "Running Total": item.running_total,
      "From Date": formatDate(item.start_date),
    }));
    const ws = XLSX.utils.json_to_sheet(tableData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SOB_Report");
    XLSX.writeFile(wb, "SOB_Target_Report.xlsx");
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = reportData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(reportData.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="min-h-screen flex flex-col bg-white text-black text-sm font-sans">
      <Header />
      <Navbar />

      <main className="flex-1 px-4 pt-6">
        <fieldset className="border border-black p-4 mb-6">
          <h1 class="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4"><span class="text-blue-700">Sob  Target </span><span class="text-blue-500">Report</span></h1>
          <div className="flex flex-wrap gap-5 items-center justify-center">
            <div>
              <label>State</label><br />
              <select value={selectedState} onChange={(e) => setSelectedState(e.target.value)} className="w-60 p-1.5 text-sm border border-gray-300">
                <option value="">All</option>
                {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label>Division</label><br />
              <select value={selectedDivision} onChange={(e) => setSelectedDivision(e.target.value)} className="w-60 p-1.5 text-sm border border-gray-300">
                <option value="">All</option>
                {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label>Quarter</label><br />
              <select value={selectedQuarter} onChange={(e) => setSelectedQuarter(e.target.value)} className="w-60 p-1.5 text-sm border border-gray-300">
                <option value="Q1-2024">Q1 (Apr - Jun) 2024</option>
                <option value="Q2-2024">Q2 (Jul - Sep) 2024</option>
                <option value="Q3-2024">Q3 (Oct - Dec) 2024</option>
                <option value="Q4-2024">Q4 (Jan - Mar) 2025</option>
                <option value="Q1-2025">Q1 (Apr - Jun) 2025</option>
                <option value="Q2-2025">Q2 (Jul - Sep) 2025</option>
                <option value="Q3-2025">Q3 (Oct - Dec) 2025</option>
                <option value="Q4-2025">Q4 (Jan - Mar) 2026</option>
              </select>
            </div>
            <div>
              <CustomButton onClick={generateReport} label="Go" variant="go" />
            </div>
          </div>
        </fieldset>

        {error && <div className="text-red-600 font-medium mb-4">{error}</div>}

        {reportData.length > 0 && (
          <fieldset className="border border-black p-4 mb-6">
            <div ref={printRef}>
              <table className="w-full border border-black border-collapse text-sm">
                <thead className="bg-blue-100">
                  <tr>
                    {['S.No', 'Empid', 'Employee', 'Division', 'State', 'HQ', 'Designation', 'DOJ', 'DOR', 'Phone', 'Target', 'From Date'].map((h, i) => (
                      <th key={i} className="border border-black p-1.5">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, i) => (
                    <tr key={i}>
                      <td className="border border-black p-1.5">{indexOfFirstItem + i + 1}</td>
                      <td className="border border-black p-1.5">{item.employee_id}</td>
                      <td className="border border-black p-1.5">{item.employee_name}</td>
                      <td className="border border-black p-1.5">{item.division}</td>
                      <td className="border border-black p-1.5">{item.state}</td>
                      <td className="border border-black p-1.5">{item.hq}</td>
                      <td className="border border-black p-1.5">{item.designation}</td>
                      <td className="border border-black p-1.5">{formatDate(item.final_joining_date)}</td>
                      <td className="border border-black p-1.5">{formatDate(item.resign_date)}</td>
                      <td className="border border-black p-1.5">{item.phone_no || '-'}</td>
                      <td className="border border-black p-1.5 text-right">{formatCurrency(item.target)}</td>
                      <td className="border border-black p-1.5">{formatDate(item.start_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 bg-gray-100 border-t border-gray-300 mt-4">
                <div className="flex items-center mb-2 sm:mb-0">
                  <label htmlFor="itemsPerPage" className="mr-2 font-medium text-sm text-gray-700">View</label>
                  <select
                    id="itemsPerPage"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setCurrentPage(1);
                      setItemsPerPage(parseInt(e.target.value));
                    }}
                    className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
                  >
                    {[10, 20, 30, 50].map((count) => (
                      <option key={count} value={count}>{count}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-600 font-medium">
                    {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, reportData.length)} of {reportData.length}
                  </span>
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    &larr;
                  </button>
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    &rarr;
                  </button>
                </div>
              </div>

              <div className="sob-button-container mt-4 flex gap-4 justify-center">
                <CustomButton onClick={handlePrint} label="Print" variant="print" />
                <CustomButton onClick={handleExportExcel} label="Export" variant="export" />
              </div>
            </div>
          </fieldset>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default SOBTargetReport;